import { z } from 'zod';
import { subjectFamily } from './content.js';
/**
 * Three medal finishes, drawn from the mascot's own gradients — the medal is the
 * mascot, so the award and the teacher are the same shape (`design/09-badges`).
 *
 * `streak` is the mascot's hot gradient, which keeps orange the streak's and only
 * the streak's. `mastery` is gold rather than the mockup's green: `009` §4 reserves
 * green for a correct answer, and a medal is not a verdict.
 */
export const badgeToneSchema = z.enum(['brand', 'streak', 'mastery']);
/**
 * Which glyph the medal wears. A family shares one and the finish says how far in
 * the learner is — glyph is *what*, finish is *how far*. A closed set rather than a
 * string, so it can be checked against `packages/ui`'s icon union.
 */
export const badgeGlyphSchema = z.enum([
    'check',
    'flame',
    'price',
    'chart',
    'mic',
    'route',
]);
/**
 * The wall, in the order it is drawn.
 *
 * Twelve rather than the mockup's twenty-four: every one here is derivable from a
 * projection that exists. Badges tied to a *particular* lesson — which is what the
 * mockup's illustrative set implies — need a product decision about which lessons
 * deserve one, and that is recorded in `docs/decisions/deferred.md` rather than
 * guessed.
 */
export const BADGES = [
    {
        id: 'first-lesson',
        name: 'First call',
        hint: 'Finish your first lesson.',
        tone: 'brand',
        glyph: 'check',
        criterion: { kind: 'lessons', atLeast: 1 },
    },
    {
        id: 'streak-3',
        name: '3-day streak',
        hint: 'Learn three days in a row.',
        tone: 'streak',
        glyph: 'flame',
        criterion: { kind: 'streak', atLeast: 3 },
    },
    {
        id: 'streak-7',
        name: '7-day streak',
        hint: 'Learn seven days in a row.',
        tone: 'streak',
        glyph: 'flame',
        criterion: { kind: 'streak', atLeast: 7 },
    },
    {
        id: 'streak-30',
        name: '30-day streak',
        hint: 'Learn thirty days in a row.',
        tone: 'streak',
        glyph: 'flame',
        criterion: { kind: 'streak', atLeast: 30 },
    },
    {
        id: 'streak-100',
        name: '100-day streak',
        hint: 'Learn a hundred days in a row.',
        tone: 'streak',
        glyph: 'flame',
        criterion: { kind: 'streak', atLeast: 100 },
    },
    {
        id: 'growth-first',
        name: 'Held the price',
        hint: 'Get a pricing, marketing or strategy question right.',
        tone: 'brand',
        glyph: 'price',
        criterion: { kind: 'family', family: 'growth', correctAtLeast: 1 },
    },
    {
        id: 'finance-first',
        name: 'Read a P&L',
        hint: 'Get a finance, data or operations question right.',
        tone: 'brand',
        glyph: 'chart',
        criterion: { kind: 'family', family: 'finance', correctAtLeast: 1 },
    },
    {
        id: 'people-first',
        name: 'Spoke up',
        hint: 'Get a leadership, sales or speaking question right.',
        tone: 'brand',
        glyph: 'mic',
        criterion: { kind: 'family', family: 'people', correctAtLeast: 1 },
    },
    {
        id: 'growth-deep',
        name: 'Ran a price war',
        hint: 'Get twenty-five growth questions right.',
        tone: 'mastery',
        glyph: 'price',
        criterion: { kind: 'family', family: 'growth', correctAtLeast: 25 },
    },
    {
        id: 'finance-deep',
        name: 'Defended a number',
        hint: 'Get twenty-five finance questions right.',
        tone: 'mastery',
        glyph: 'chart',
        criterion: { kind: 'family', family: 'finance', correctAtLeast: 25 },
    },
    {
        id: 'people-deep',
        name: 'Had the hard conversation',
        hint: 'Get twenty-five people questions right.',
        tone: 'mastery',
        glyph: 'mic',
        criterion: { kind: 'family', family: 'people', correctAtLeast: 25 },
    },
    {
        id: 'path-done',
        name: 'Finished a path',
        hint: 'Complete every lesson in one path.',
        tone: 'mastery',
        glyph: 'route',
        criterion: { kind: 'paths', atLeast: 1 },
    },
];
/** Sums `SubjectMastery.correct` into the three families `009` §4 ships. */
export function correctByFamily(rows) {
    const totals = {
        growth: 0,
        finance: 0,
        people: 0,
    };
    for (const row of rows) {
        totals[subjectFamily[row.subject]] += row.correct;
    }
    return totals;
}
/** Whether one criterion is met. Exhaustive over `BadgeCriterion` by construction. */
function meets(criterion, facts) {
    switch (criterion.kind) {
        case 'lessons':
            return facts.lessonsCompleted >= criterion.atLeast;
        case 'streak':
            return facts.streakLongest >= criterion.atLeast;
        case 'family':
            return (facts.correctByFamily[criterion.family] >= criterion.correctAtLeast);
        case 'paths':
            return facts.pathsCompleted >= criterion.atLeast;
    }
}
export const badgeSchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1).max(40),
    hint: z.string().min(1).max(120),
    tone: badgeToneSchema,
    glyph: badgeGlyphSchema,
    earned: z.boolean(),
})
    .describe('One tile on the wall, earned or not.');
export const badgeWallSchema = z
    .object({
    /**
     * Every badge, earned and locked, in catalogue order. One array rather than two,
     * because the screen draws two sections from it and a client handed two lists
     * could not tell which order the catalogue meant.
     */
    badges: z.array(badgeSchema),
    earnedCount: z.number().int().min(0),
    totalCount: z.number().int().min(0),
    /** The hero row. The same numbers Home shows, so the two cannot disagree. */
    xpTotal: z.number().int().min(0),
    streakCurrent: z.number().int().min(0),
})
    .describe('Everything the You screen shows (design/09-badges).');
/** The catalogue, resolved against one learner's projections. */
export function badgesFor(facts) {
    return BADGES.map((badge) => ({
        id: badge.id,
        name: badge.name,
        hint: badge.hint,
        tone: badge.tone,
        glyph: badge.glyph,
        earned: meets(badge.criterion, facts),
    }));
}
//# sourceMappingURL=badges.js.map